import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie, Shield, BarChart3, Megaphone, X, Check } from "lucide-react";

/**
 * GDPR / CCPA-compliant cookie consent banner.
 * Stores user choice in localStorage under "isaiah_cookie_consent_v1".
 * Categories: essential (always on), analytics, marketing.
 * Emits a custom event "isaiah-cookie-consent-changed" so other scripts
 * can listen and enable/disable tracking accordingly.
 */
const STORAGE_KEY = "isaiah_cookie_consent_v1";
const VERSION = 1;

export const getCookieConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const hasConsent = (category) => {
  const c = getCookieConsent();
  if (!c) return false;
  if (category === "essential") return true;
  return !!c.categories?.[category];
};

const saveConsent = (categories, mode) => {
  const record = {
    version: VERSION,
    mode, // 'accept_all' | 'reject_all' | 'custom'
    categories: { essential: true, ...categories },
    timestamp: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  try {
    window.dispatchEvent(
      new CustomEvent("isaiah-cookie-consent-changed", { detail: record })
    );
  } catch { /* no-op */ }
  return record;
};

const CookieBanner = () => {
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [categories, setCategories] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    // E2E / smoke-test bypass: ?e2e=1 in the URL auto-accepts all cookies so
    // headless Playwright runs aren't blocked by the consent dialog when
    // attempting to authenticate. No visual change for real users.
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("e2e") === "1") {
        saveConsent({ analytics: true, marketing: true }, "accept_all");
        return;
      }
    } catch { /* no-op */ }
    const existing = getCookieConsent();
    if (!existing) {
      // Slight delay so it doesn't slam on first paint
      const t = setTimeout(() => setShow(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  // Allow "Manage cookies" link from footer to re-open the banner
  useEffect(() => {
    const onOpen = () => {
      const existing = getCookieConsent();
      setCategories({
        analytics: existing?.categories?.analytics ?? false,
        marketing: existing?.categories?.marketing ?? false,
      });
      setExpanded(true);
      setShow(true);
    };
    window.addEventListener("isaiah-open-cookie-banner", onOpen);
    return () => window.removeEventListener("isaiah-open-cookie-banner", onOpen);
  }, []);

  if (!show) return null;

  // Inside the kid-facing Learning Academy we suppress the banner so it never
  // covers the learning surfaces. Consent state is preserved, it simply
  // re-appears (if still un-chosen) once the child leaves the Academy.
  if (location.pathname.startsWith("/learn/academy") || location.pathname.startsWith("/academy")) {
    return null;
  }

  // On the Tutor chat we move the banner to the TOP so it never overlaps
  // the chat input bar (which lives at the bottom of the viewport). Kids
  // expect to tap the input + paperclip immediately on entering the page.
  // Everywhere else (incl. auth pages) it stays bottom-centered.
  // Pin the banner to the TOP (instead of bottom-center) wherever the bottom of
  // the viewport holds a critical control the banner must not overlap: the Tutor
  // chat input, and the auth/onboarding forms (Terms checkbox + Create Account
  // CTA). Everywhere else it stays bottom-centered.
  const pinTopPath = typeof window !== "undefined" &&
    /^\/(learn\/tutor|signup|login|complete-profile|portal\/(register|onboarding))/.test(window.location.pathname);
  const pinTop = pinTopPath;

  const handleAcceptAll = () => {
    saveConsent({ analytics: true, marketing: true }, "accept_all");
    setShow(false);
  };
  const handleRejectAll = () => {
    saveConsent({ analytics: false, marketing: false }, "reject_all");
    setShow(false);
  };
  const handleSave = () => {
    saveConsent(categories, "custom");
    setShow(false);
  };

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-desc"
      style={{
        position: "fixed",
        left: pinTop ? "50%" : "1rem",
        bottom: pinTop ? "auto" : "1rem",
        top: pinTop ? "0.5rem" : "auto",
        transform: pinTop ? "translateX(-50%)" : "none",
        width: pinTop ? "min(540px, calc(100% - 1.5rem))" : "min(420px, calc(100% - 2rem))",
        zIndex: 9998,
        background: "linear-gradient(180deg, rgba(15,20,40,0.98), rgba(8,10,24,0.99))",
        border: "1px solid rgba(139,92,246,0.35)",
        borderRadius: "16px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.55), 0 0 30px rgba(139,92,246,0.18)",
        padding: pinTop ? "0.55rem 0.9rem" : "1.1rem 1.25rem",
        color: "white",
      }}
    >
      <div className="flex items-start gap-3">
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            background: "rgba(251,146,60,0.15)",
            border: "1px solid rgba(251,146,60,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Cookie size={18} style={{ color: "#FB923C" }} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 id="cookie-banner-title" className="text-white font-fredoka font-bold text-sm sm:text-base">
            Your privacy matters
          </h2>
          <p id="cookie-banner-desc" className="text-white/60 text-xs sm:text-[13px] mt-1 leading-relaxed">
            We use cookies to keep the site running, remember your progress, and understand how explorers use Isaiah&apos;s World. Essential cookies are always on. You choose the rest.
            {" "}
            <Link to="/privacy" className="text-blue-400 hover:underline" data-testid="cookie-banner-privacy-link">Privacy Policy</Link>
          </p>

          {expanded && (
            <div className="mt-3 space-y-2" data-testid="cookie-categories">
              <CategoryRow
                icon={<Shield size={14} className="text-emerald-400" />}
                title="Essential"
                desc="Login, security, session, required."
                enabled
                locked
                testid="cookie-cat-essential"
              />
              <CategoryRow
                icon={<BarChart3 size={14} className="text-blue-400" />}
                title="Analytics"
                desc="Anonymous usage data so we can improve the platform."
                enabled={categories.analytics}
                onChange={(v) => setCategories((c) => ({ ...c, analytics: v }))}
                testid="cookie-cat-analytics"
              />
              <CategoryRow
                icon={<Megaphone size={14} className="text-pink-400" />}
                title="Marketing"
                desc="Helps us tell other families about Isaiah's World. Never used for behavioural ads to kids."
                enabled={categories.marketing}
                onChange={(v) => setCategories((c) => ({ ...c, marketing: v }))}
                testid="cookie-cat-marketing"
              />
            </div>
          )}

          <div className="flex flex-wrap gap-2 mt-3">
            {!expanded && (
              <button
                onClick={() => setExpanded(true)}
                className="px-3 py-1.5 rounded-lg border border-white/15 text-white/70 hover:text-white hover:bg-white/5 text-xs font-semibold transition-all"
                data-testid="cookie-banner-customize"
              >
                Customize
              </button>
            )}
            {expanded && (
              <button
                onClick={handleSave}
                className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white text-xs font-semibold transition-all"
                data-testid="cookie-banner-save"
              >
                <Check size={12} className="inline mr-1" /> Save choices
              </button>
            )}
            <button
              onClick={handleRejectAll}
              className="px-3 py-1.5 rounded-lg border border-white/15 text-white/70 hover:text-white hover:bg-white/5 text-xs font-semibold transition-all"
              data-testid="cookie-banner-reject"
            >
              Reject all
            </button>
            <button
              onClick={handleAcceptAll}
              className="px-3 py-1.5 rounded-lg text-white text-xs font-bold transition-all ml-auto"
              style={{ background: "linear-gradient(135deg,#F97316,#EC4899)" }}
              data-testid="cookie-banner-accept"
            >
              Accept all
            </button>
          </div>
        </div>
        <button
          onClick={handleRejectAll}
          aria-label="Close and reject non-essential cookies"
          className="text-white/30 hover:text-white/70 transition-colors flex-shrink-0"
          data-testid="cookie-banner-close"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

const CategoryRow = ({ icon, title, desc, enabled, onChange, locked, testid }) => (
  <label
    className="flex items-center gap-3 p-2.5 rounded-lg border border-white/10 bg-white/5"
    data-testid={testid}
  >
    <div style={{ width: 24, display: "flex", justifyContent: "center" }}>{icon}</div>
    <div className="flex-1 min-w-0">
      <p className="text-white text-xs font-semibold">{title}</p>
      <p className="text-white/45 text-[10px] leading-snug">{desc}</p>
    </div>
    <div style={{ flexShrink: 0 }}>
      {locked ? (
        <span className="text-emerald-400 text-[10px] font-bold uppercase tracking-wider">Always</span>
      ) : (
        <input
          type="checkbox"
          checked={!!enabled}
          onChange={(e) => onChange?.(e.target.checked)}
          className="w-4 h-4 rounded accent-purple-500 cursor-pointer"
          data-testid={`${testid}-toggle`}
        />
      )}
    </div>
  </label>
);

export default CookieBanner;
